import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import { loanService } from '../../services/loanService';
import { memberService } from '../../services/memberService';
import toast from 'react-hot-toast';

const LoanApplication = () => {
  const navigate = useNavigate();
  const [selectedMember, setSelectedMember] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [formData, setFormData] = useState({
    member_id: '',
    product_id: '',
    principal_amount: '',
    duration_months: '',
    purpose: '',
    guarantor_name: '',
    guarantor_phone: '',
    remarks: ''
  });

  const [calculatedEMI, setCalculatedEMI] = useState(null);

  // Fetch loan products
  const { data: productsData, isLoading: productsLoading } = useQuery(
    'loan-products',
    () => loanService.getLoanProducts(),
    {
      enabled: true,
      onError: (error) => {
        console.error('Failed to load products:', error);
        toast.error('Failed to load loan products');
      }
    }
  );

  const products = productsData?.data || [];

  // Search members
  const searchMembers = async () => {
    if (searchTerm.length >= 2) {
      setIsSearching(true);
      try {
        const result = await memberService.searchMembers(searchTerm);
        setSearchResults(result.data || []);
      } catch (error) {
        console.error('Search error:', error);
        toast.error('Failed to search members');
      } finally {
        setIsSearching(false);
      }
    } else {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (searchTerm) searchMembers();
    }, 500);
    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Calculate EMI when form changes
  useEffect(() => {
    if (formData.principal_amount && formData.duration_months && formData.product_id) {
      const product = products.find(p => p.id === parseInt(formData.product_id));
      if (product) {
        const principal = parseFloat(formData.principal_amount);
        const rate = product.default_interest_rate;
        const months = parseInt(formData.duration_months);

        if (principal > 0 && months > 0) {
          const monthlyRate = rate / 100 / 12;
          let emi, totalPayable, totalInterest;

          if (monthlyRate === 0) {
            emi = principal / months;
          } else {
            emi = principal * monthlyRate * Math.pow(1 + monthlyRate, months) /
                   (Math.pow(1 + monthlyRate, months) - 1);
          }
          totalPayable = emi * months;
          totalInterest = totalPayable - principal;

          setCalculatedEMI({
            emi: emi.toFixed(2),
            totalPayable: totalPayable.toFixed(2),
            totalInterest: totalInterest.toFixed(2)
          });
        }
      }
    }
  }, [formData.principal_amount, formData.duration_months, formData.product_id, products]);

  const applyMutation = useMutation(
    (data) => loanService.applyForLoan(data),
    {
      onSuccess: (response) => {
        toast.success(response.message || 'Loan application submitted successfully!');
        navigate('/loans');
      },
      onError: (error) => {
        console.error('Application error:', error);

        // Handle different error formats
        let errorMessage = 'Application failed';

        if (error.response?.data?.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response?.data?.errors) {
          // Handle validation errors array
          const errors = error.response.data.errors;
          if (Array.isArray(errors) && errors.length > 0) {
            errorMessage = errors[0].msg || errors[0].message || 'Validation error';
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        toast.error(errorMessage);
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.member_id) {
      toast.error('Please select a member');
      return;
    }

    if (!formData.product_id) {
      toast.error('Please select a loan product');
      return;
    }

    if (!formData.principal_amount || parseFloat(formData.principal_amount) <= 0) {
      toast.error('Please enter valid loan amount');
      return;
    }

    if (!formData.duration_months) {
      toast.error('Please select loan duration');
      return;
    }

    // Prepare data for API
    const submitData = {
      member_id: parseInt(formData.member_id),
      product_id: parseInt(formData.product_id),
      principal_amount: parseFloat(formData.principal_amount),
      duration_months: parseInt(formData.duration_months),
      purpose: formData.purpose || null,
      guarantor_name: formData.guarantor_name || null,
      guarantor_phone: formData.guarantor_phone || null,
      remarks: formData.remarks || null
    };

    applyMutation.mutate(submitData);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const selectMember = (member) => {
    setFormData({ ...formData, member_id: member.id });
    setSelectedMember(`${member.name} (${member.member_code})`);
    setSearchTerm('');
    setSearchResults([]);
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-6">Loan Application</h1>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Member Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">1. Select Member</h2>

          {!formData.member_id ? (
            <div>
              <input
                type="text"
                placeholder="Search by name, phone, or member code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {isSearching && <div className="mt-2 text-gray-500">Searching...</div>}
              {searchResults.length > 0 && (
                <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                  {searchResults.map(member => (
                    <div
                      key={member.id}
                      onClick={() => selectMember(member)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b"
                    >
                      <div className="font-medium">{member.name}</div>
                      <div className="text-sm text-gray-500">
                        Code: {member.member_code} | Phone: {member.phone}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {searchTerm.length >= 2 && !isSearching && searchResults.length === 0 && (
                <div className="mt-2 text-gray-500">No members found</div>
              )}
            </div>
          ) : (
            <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
              <div>
                <div className="font-medium">{selectedMember}</div>
                <div className="text-sm text-gray-600">Member selected</div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData({ ...formData, member_id: '' });
                  setSelectedMember('');
                }}
                className="text-red-600 hover:text-red-800"
              >
                Change
              </button>
            </div>
          )}
        </div>

        {/* Loan Details */}
        {formData.member_id && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">2. Loan Details</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Loan Product *</label>
                <select
                  name="product_id"
                  value={formData.product_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                  disabled={productsLoading}
                >
                  <option value="">Select Product</option>
                  {products.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.product_name} - {product.default_interest_rate}%
                      (Min: {product.min_amount}, Max: {product.max_amount})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Loan Amount *</label>
                <input
                  type="number"
                  name="principal_amount"
                  value={formData.principal_amount}
                  onChange={handleChange}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Duration (Months) *</label>
                <select
                  name="duration_months"
                  value={formData.duration_months}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="">Select Duration</option>
                  {[6, 12, 18, 24, 30, 36, 48, 60].map(months => (
                    <option key={months} value={months}>{months} months</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Purpose</label>
                <input
                  type="text"
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleChange}
                  placeholder="e.g., Business, Education, Emergency"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* EMI Calculation Result */}
            {calculatedEMI && (
              <div className="mt-6 p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold mb-2">Loan Summary</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-sm text-gray-600">Monthly EMI</div>
                    <div className="text-xl font-bold text-green-600">৳ {parseFloat(calculatedEMI.emi).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Payable</div>
                    <div className="text-xl font-bold text-blue-600">৳ {parseFloat(calculatedEMI.totalPayable).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600">Total Interest</div>
                    <div className="text-xl font-bold text-orange-600">৳ {parseFloat(calculatedEMI.totalInterest).toLocaleString()}</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Guarantor Information */}
        {formData.member_id && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">3. Guarantor Information</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-2">Guarantor Name</label>
                <input
                  type="text"
                  name="guarantor_name"
                  value={formData.guarantor_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Guarantor Phone</label>
                <input
                  type="tel"
                  name="guarantor_phone"
                  value={formData.guarantor_phone}
                  onChange={handleChange}
                  placeholder="01XXXXXXXXX"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Additional Remarks</label>
              <textarea
                name="remarks"
                value={formData.remarks}
                onChange={handleChange}
                rows="3"
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Any additional information about the loan application..."
              />
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {formData.member_id && (
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={() => navigate('/loans')}
              className="px-4 py-2 border rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={applyMutation.isLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {applyMutation.isLoading ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default LoanApplication;